import { IAvailabilityData } from "types/appointmentBooking";
import { AppointmentType, UpcomingAppointmentDto } from "types/generated/models";
import { Box, Grid } from "@mui/material";
import { Typography } from "@powerdiary/components-library/lib";
import { CalendarDays, IdCardLanyard, LocateFixed, UserRound, UsersRound } from "lucide-react";
import ConfirmationDetailCard from "../../../Shared/ConfirmationDetailCard";
import { getServiceValue } from "../../../../utils/serviceValue";
import ConfirmationNotesCard from "../../../Shared/ConfirmationNotesCard";
import { getTimeZoneAbbreviation } from "utils/timeZoneUtils";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import { getGroupBookingLabel } from "../../../../utils/groupBookingLabel";

interface IConfirmationProps {
    selectedAvailability: IAvailabilityData;
    appointment: UpcomingAppointmentDto | null;
    notes: string;
    setNotes: (notes: string) => void;
}

export default function Confirmation({ selectedAvailability, appointment, notes, setNotes }: IConfirmationProps) {
    const {
        clientPortalConfig: { groupBookingLabel },
    } = useClientPortalConfigContext();
    const configuredGroupBookingLabel = getGroupBookingLabel(groupBookingLabel);

    return (
        <Box
            sx={(theme) => ({
                padding: "16px 0",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                [theme.breakpoints.down("sm")]: {
                    padding: "16px",
                },
            })}
            data-test-id="section_appointment_reschedule_confirmation"
        >
            <Typography variant="heading4">Take a moment to review your booking details</Typography>

            <Grid container spacing={2} rowSpacing={1} sx={{ width: "100%" }}>
                <Grid minWidth="344px" size={{ xs: 3 }}>
                    <ConfirmationDetailCard title="Location" content={appointment.location} icon={LocateFixed} />
                </Grid>
                <Grid minWidth="344px" size={{ xs: 3 }}>
                    <ConfirmationDetailCard title="Provider" content={appointment.provider} icon={IdCardLanyard} />
                </Grid>
                <Grid minWidth="344px" size={{ xs: 3 }}>
                    <ConfirmationDetailCard
                        title="When"
                        content={selectedAvailability.displayText}
                        contentSuffix={getTimeZoneAbbreviation(selectedAvailability.timeEntry?.dateTimeOffset)}
                        icon={CalendarDays}
                    />
                </Grid>
                <Grid minWidth="344px" size={{ xs: 3 }}>
                    <ConfirmationDetailCard
                        title="Service"
                        content={getServiceValue(appointment.service)}
                        icon={UserRound}
                    />
                </Grid>
                <Grid minWidth="344px" size={{ xs: 3 }}>
                    <ConfirmationNotesCard
                        notes={notes}
                        onNotesChange={(note) => setNotes(note)}
                        isBookingCompleted={false}
                    />
                </Grid>
                {appointment.appointmentType === AppointmentType.GroupAppointment && (
                    <Grid minWidth="344px" size={{ xs: 3 }}>
                        <ConfirmationDetailCard
                            title={configuredGroupBookingLabel}
                            content={appointment.groupDescription || ""}
                            icon={UsersRound}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}
